export const EV_ENTRY_PATH = '/ev'
export const EV_STATIONS_PATH = '/ev/estacoes'
const EV_STATION_PREFIX = '/ev/estacao/'
const EV_QR_PREFIX = '/ev/c/'
const SAFE_EV_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,99}$/
const SAFE_EV_QR_CODE = /^EV-[a-f0-9]{32}$/
const PENDING_EV_PATH_KEY = 'fidelidade_ev_pending_path'
const PENDING_EV_CONNECTOR_KEY = 'fidelidade_ev_pending_connector'

export interface EvConnectorSelection {
	estacao_id: string
	conector_id: string
}

function clean_path(path: string): string {
	const without_query = String(path || '').split('?')[0] ?? ''
	return (without_query.split('#')[0] ?? '') || '/'
}

export function is_ev_flow_path(path: string): boolean {
	const clean = clean_path(path)
	return clean === EV_ENTRY_PATH
		|| clean === EV_STATIONS_PATH
		|| valid_dynamic_path(clean, EV_STATION_PREFIX)
		|| valid_qr_path(clean)
}

function valid_dynamic_path(path: string, prefix: string): boolean {
	if (!path.startsWith(prefix)) return false
	return parse_ev_path_identifier(path.slice(prefix.length)) !== null
}

function valid_qr_path(path: string): boolean {
	if (!path.startsWith(EV_QR_PREFIX)) return false
	return parse_ev_qr_identifier(path.slice(EV_QR_PREFIX.length)) !== null
}

export function parse_ev_path_identifier(raw_value: string | undefined): string | null {
	if (!raw_value || raw_value.includes('/')) return null
	try {
		const value = decodeURIComponent(raw_value)
		return SAFE_EV_ID.test(value) ? value : null
	} catch {
		return null
	}
}

export function parse_ev_qr_identifier(raw_value: string | undefined): string | null {
	if (!raw_value || raw_value.includes('/')) return null
	try {
		const value = decodeURIComponent(raw_value)
		return SAFE_EV_QR_CODE.test(value) ? value : null
	} catch {
		return null
	}
}

export function ev_station_path(station_id: string, connector_id?: string): string {
	if (!SAFE_EV_ID.test(station_id)) throw new Error('Estação inválida')
	if (connector_id !== undefined && !SAFE_EV_ID.test(connector_id)) throw new Error('Conector inválido')
	const path = `${EV_STATION_PREFIX}${encodeURIComponent(station_id)}`
	return connector_id === undefined ? path : `${path}?conector=${encodeURIComponent(connector_id)}`
}

export function ev_qr_path(code: string): string {
	if (!SAFE_EV_QR_CODE.test(code)) throw new Error('Código QR inválido')
	return `${EV_QR_PREFIX}${encodeURIComponent(code)}`
}

export function parse_ev_qr_scan_value(raw_value: string): string | null {
	const raw = String(raw_value || '').trim()
	if (!raw || raw.length > 2048) return null
	if (SAFE_EV_QR_CODE.test(raw)) return raw

	let parsed: URL
	try {
		parsed = new URL(raw)
	} catch {
		return null
	}
	if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password || parsed.search || parsed.hash) return null
	if (!parsed.pathname.startsWith(EV_QR_PREFIX)) return null
	return parse_ev_qr_identifier(parsed.pathname.slice(EV_QR_PREFIX.length))
}

function safe_pending_path(path: string): string | null {
	let parsed: URL
	try {
		parsed = new URL(path, window.location.origin)
	} catch {
		return null
	}
	if (parsed.origin !== window.location.origin || parsed.hash) return null
	const clean = parsed.pathname
	const keys = [...parsed.searchParams.keys()]
	if (keys.some(key => key !== 'conector' && key !== 'pin')) return null
	if (parsed.searchParams.getAll('pin').length > 1) return null
	if (valid_qr_path(clean)) {
		return parsed.searchParams.has('conector') ? null : clean
	}
	if (!valid_dynamic_path(clean, EV_STATION_PREFIX)) return null
	if (parsed.searchParams.getAll('conector').length > 1) return null
	const connector = parsed.searchParams.get('conector')
	if (connector !== null && !SAFE_EV_ID.test(connector)) return null
	return connector === null ? clean : `${clean}?conector=${encodeURIComponent(connector)}`
}

export function remember_ev_return_path(path: string): boolean {
	const safe_path = safe_pending_path(path)
	if (!safe_path) return false
	sessionStorage.setItem(PENDING_EV_PATH_KEY, safe_path)
	return true
}

export function consume_ev_return_path(): string | null {
	const saved = sessionStorage.getItem(PENDING_EV_PATH_KEY)
	sessionStorage.removeItem(PENDING_EV_PATH_KEY)
	return saved === null ? null : safe_pending_path(saved)
}

export function remember_ev_connector_selection(station_id: string, connector_id: string): boolean {
	if (!SAFE_EV_ID.test(station_id) || !SAFE_EV_ID.test(connector_id)) return false
	sessionStorage.setItem(PENDING_EV_CONNECTOR_KEY, JSON.stringify({ estacao_id: station_id, conector_id: connector_id }))
	return true
}

export function consume_ev_connector_selection(): EvConnectorSelection | null {
	const saved = sessionStorage.getItem(PENDING_EV_CONNECTOR_KEY)
	sessionStorage.removeItem(PENDING_EV_CONNECTOR_KEY)
	if (!saved) return null
	try {
		const value = JSON.parse(saved) as unknown
		if (!value || typeof value !== 'object' || Array.isArray(value)) return null
		const selection = value as Record<string, unknown>
		if (typeof selection.estacao_id !== 'string' || typeof selection.conector_id !== 'string') return null
		if (!SAFE_EV_ID.test(selection.estacao_id) || !SAFE_EV_ID.test(selection.conector_id)) return null
		return { estacao_id: selection.estacao_id, conector_id: selection.conector_id }
	} catch {
		return null
	}
}
